import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  TrendingDown,
  TrendingUp,
  DollarSign,
  Zap,
  AlertCircle,
  CheckCircle,
  BarChart3,
  Settings
} from 'lucide-react';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';
import { Button } from '@shared/components/ui/Button';
import {
  generateParameterRecommendations,
  LLMParameters,
  formatParameters,
  getOutOfRangeParameters
} from '../../utils/llmParameterHelpers';

interface OptimizationInsight {
  configuration_id: string;
  model: string;
  prompt_name: string;
  parameters: LLMParameters;
  optimization_score: number;
  token_efficiency_score: number;
  success_rate: number;
  avg_response_time_ms: number;
  total_calls: number;
  avg_cost_per_call: number;
  pending_recommendation: any;
  last_optimized_at: string | null;
  last_used_at: string | null;
}

interface Recommendation {
  id: string;
  configuration_id: string;
  prompt_id: string;
  current_parameters: LLMParameters;
  recommended_parameters: Partial<LLMParameters>;
  reasoning: string;
  potential_savings_usd: number;
  confidence_score: number;
  based_on_metrics_count: number;
  status: string;
  created_at: string;
}

const AdminLLMOptimization: React.FC = () => {
  const navigate = useNavigate();
  const [insights, setInsights] = useState<OptimizationInsight[]>([]);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    loadOptimizationData();
  }, []);

  const loadOptimizationData = async () => {
    try {
      setLoading(true);

      const [insightsResult, recommendationsResult] = await Promise.all([
        supabase
          .from('llm_optimization_insights')
          .select('*')
          .order('optimization_score', { ascending: true })
          .limit(20),
        supabase
          .from('llm_optimization_recommendations')
          .select('*')
          .eq('status', 'pending')
          .order('potential_savings_usd', { ascending: false })
      ]);

      if (insightsResult.error) {
        if (insightsResult.error.code === 'PGRST116' || insightsResult.error.message?.includes('does not exist')) {
          console.error('LLM optimization tables not found. Please run database migrations.');
          toast.error('Optimization feature requires database setup. Please contact your administrator.');
          return;
        }
        throw insightsResult.error;
      }

      if (recommendationsResult.error) {
        if (recommendationsResult.error.code === 'PGRST116' || recommendationsResult.error.message?.includes('does not exist')) {
          console.error('LLM optimization tables not found. Please run database migrations.');
          toast.error('Optimization feature requires database setup. Please contact your administrator.');
          return;
        }
        throw recommendationsResult.error;
      }

      setInsights(insightsResult.data || []);
      setRecommendations(recommendationsResult.data || []);
    } catch (error: any) {
      console.error('Error loading optimization data:', error);
      const errorMessage = error?.message || 'Unknown error occurred';

      if (errorMessage.includes('relation') && errorMessage.includes('does not exist')) {
        toast.error('Optimization database tables are missing. Please run migrations.');
      } else {
        toast.error('Failed to load optimization insights');
      }
    } finally {
      setLoading(false);
    }
  };

  const generateRecommendations = async () => {
    try {
      setGenerating(true);
      toast.info('Analyzing configurations and generating recommendations...');

      // Get all configurations with metrics
      const { data: configs, error: configsError } = await supabase
        .from('llm_configurations')
        .select(`
          id,
          prompt_id,
          parameters,
          success_rate,
          avg_response_time_ms,
          total_calls
        `)
        .gte('total_calls', 10); // Only configs with sufficient data

      if (configsError) throw configsError;

      let recommendationsCreated = 0;

      for (const config of configs || []) {
        // Get usage metrics for this config
        const { data: metrics } = await supabase
          .from('llm_usage_metrics')
          .select('tokens_total, cost_usd')
          .eq('prompt_id', config.prompt_id)
          .order('created_at', { ascending: false })
          .limit(100);

        if (!metrics || metrics.length === 0) continue;

        const avgTokens = metrics.reduce((sum, m) => sum + m.tokens_total, 0) / metrics.length;
        const maxTokens = Math.max(...metrics.map(m => m.tokens_total));
        const avgCost = metrics.reduce((sum, m) => sum + parseFloat(m.cost_usd), 0) / metrics.length;

        const recommendation = generateParameterRecommendations(
          config.parameters,
          {
            avgTokensUsed: avgTokens,
            maxTokensNeeded: maxTokens,
            successRate: config.success_rate || 100,
            avgCost
          }
        );

        if (Object.keys(recommendation.recommendedParameters).length > 0) {
          // Create recommendation in database
          const { error: insertError } = await supabase
            .from('llm_optimization_recommendations')
            .insert({
              configuration_id: config.id,
              prompt_id: config.prompt_id,
              current_parameters: config.parameters,
              recommended_parameters: recommendation.recommendedParameters,
              reasoning: recommendation.reasoning.join('\n\n'),
              potential_savings_usd: recommendation.potentialSavings,
              confidence_score: 0.85,
              based_on_metrics_count: metrics.length,
              status: 'pending'
            });

          if (!insertError) {
            recommendationsCreated++;
          }
        }
      }

      toast.success(`Generated ${recommendationsCreated} optimization recommendations`);
      loadOptimizationData();
    } catch (error: any) {
      console.error('Error generating recommendations:', error);
      toast.error('Failed to generate recommendations');
    } finally {
      setGenerating(false);
    }
  };

  const applyRecommendation = async (rec: Recommendation) => {
    if (!confirm('Apply this recommendation? This will update the configuration parameters.')) {
      return;
    }

    try {
      const mergedParameters = {
        ...rec.current_parameters,
        ...rec.recommended_parameters
      };

      const { error: updateError } = await supabase
        .from('llm_configurations')
        .update({
          parameters: mergedParameters,
          last_optimized_at: new Date().toISOString()
        })
        .eq('id', rec.configuration_id);

      if (updateError) throw updateError;

      const { error: recError } = await supabase
        .from('llm_optimization_recommendations')
        .update({
          status: 'applied',
          applied_at: new Date().toISOString(),
          applied_by: (await supabase.auth.getUser()).data.user?.id
        })
        .eq('id', rec.id);

      if (recError) throw recError;

      toast.success('Recommendation applied successfully');
      loadOptimizationData();
    } catch (error: any) {
      console.error('Error applying recommendation:', error);
      toast.error('Failed to apply recommendation');
    }
  };

  const rejectRecommendation = async (recId: string) => {
    try {
      const { error } = await supabase
        .from('llm_optimization_recommendations')
        .update({ status: 'rejected' })
        .eq('id', recId);

      if (error) throw error;

      toast.success('Recommendation rejected');
      loadOptimizationData();
    } catch (error: any) {
      console.error('Error rejecting recommendation:', error);
      toast.error('Failed to reject recommendation');
    }
  };

  const calculateTotalSavings = () => {
    return recommendations.reduce((sum, rec) => sum + parseFloat(rec.potential_savings_usd.toString()), 0);
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const totalSavings = calculateTotalSavings();
  const configsNeedingOptimization = insights.filter(i => i.optimization_score < 70).length;
  const avgOptimizationScore = insights.length > 0
    ? insights.reduce((sum, i) => sum + i.optimization_score, 0) / insights.length
    : 0;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <button
          onClick={() => navigate('/admin/llm')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to LLM Configuration
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Parameter Optimization</h1>
            <p className="text-gray-600">Analyze and optimize LLM parameters for cost and performance</p>
          </div>
          <Button
            onClick={generateRecommendations}
            disabled={generating}
            className="flex items-center gap-2"
          >
            <Zap className={`w-4 h-4 ${generating ? 'animate-pulse' : ''}`} />
            {generating ? 'Analyzing...' : 'Generate Recommendations'}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Potential Savings</h3>
            <DollarSign className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">${totalSavings.toFixed(4)}</p>
          <p className="text-xs text-gray-500 mt-1">Per call across all recommendations</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Pending Actions</h3>
            <AlertCircle className="w-5 h-5 text-yellow-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{recommendations.length}</p>
          <p className="text-xs text-gray-500 mt-1">Optimization recommendations</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Needs Optimization</h3>
            <TrendingDown className="w-5 h-5 text-red-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{configsNeedingOptimization}</p>
          <p className="text-xs text-gray-500 mt-1">Configurations below 70% score</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Avg Optimization</h3>
            <BarChart3 className="w-5 h-5 text-blue-500" />
          </div>
          <p className={`text-3xl font-bold ${getScoreColor(avgOptimizationScore)}`}>
            {avgOptimizationScore.toFixed(1)}%
          </p>
          <p className="text-xs text-gray-500 mt-1">Overall system efficiency</p>
        </div>
      </div>

      {/* Pending Recommendations */}
      {recommendations.length > 0 && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-gray-200 bg-yellow-50">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-600" />
              Optimization Recommendations
            </h2>
          </div>
          <div className="divide-y divide-gray-200">
            {recommendations.map((rec) => (
              <div key={rec.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">
                        Save ${rec.potential_savings_usd.toFixed(6)}/call
                      </span>
                      <span className="text-xs text-gray-500">
                        Based on {rec.based_on_metrics_count} API calls
                      </span>
                      <span className="text-xs text-gray-500">
                        Confidence: {(rec.confidence_score * 100).toFixed(0)}%
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => applyRecommendation(rec)}
                      className="flex items-center gap-1"
                    >
                      <CheckCircle className="w-3 h-3" />
                      Apply
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => rejectRecommendation(rec.id)}
                    >
                      Reject
                    </Button>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="bg-gray-50 rounded p-3">
                    <div className="text-xs font-medium text-gray-700 mb-1">Reasoning:</div>
                    <div className="text-sm text-gray-900 whitespace-pre-wrap">{rec.reasoning}</div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-red-50 rounded p-3">
                      <div className="text-xs font-medium text-red-700 mb-2">Current Parameters</div>
                      <div className="text-xs text-gray-700 space-y-1 font-mono">
                        {Object.entries(rec.current_parameters).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span>{key}:</span>
                            <span className="font-semibold">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="bg-green-50 rounded p-3">
                      <div className="text-xs font-medium text-green-700 mb-2">Recommended Changes</div>
                      <div className="text-xs text-gray-700 space-y-1 font-mono">
                        {Object.entries(rec.recommended_parameters).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span>{key}:</span>
                            <span className="font-semibold text-green-700">
                              {rec.current_parameters[key as keyof LLMParameters]} → {value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Configuration Insights */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Configuration Performance</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prompt / Model
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Optimization Score
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Token Efficiency
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Success Rate
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Cost
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Calls
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Issues
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {insights.map((insight, index) => {
                const outOfRangeParams = getOutOfRangeParameters(insight.parameters);

                return (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{insight.prompt_name}</div>
                      <div className="text-xs text-gray-500">{insight.model}</div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${getScoreBgColor(insight.optimization_score)} ${getScoreColor(insight.optimization_score)}`}>
                        {insight.optimization_score.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-sm text-gray-900">
                        {insight.token_efficiency_score.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-sm text-gray-900">
                        {insight.success_rate?.toFixed(1) || 'N/A'}%
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-sm text-gray-900">
                        ${insight.avg_cost_per_call?.toFixed(6) || '0.000000'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-sm text-gray-600">{insight.total_calls}</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {outOfRangeParams.length > 0 ? (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-yellow-100 text-yellow-800">
                          {outOfRangeParams.length} warning{outOfRangeParams.length > 1 ? 's' : ''}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">None</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {insights.length === 0 && (
            <div className="text-center py-12">
              <Settings className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No optimization data available yet</p>
              <p className="text-sm text-gray-400 mt-1">Data will populate as configurations are used</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLLMOptimization;
